<template>
  <div class="tnm-training-plan-list">
    <v-card>
      <!-- 頁面標題列（共用元件，融入卡片頂部） -->
      <ListPageHeader
        embedded
        title="培訓計畫申請清單"
        subtitle="審議與管理各協會培訓計畫申請"
        icon="tabler-clipboard-check"
        :count="filteredRows.length"
      >
        <template #actions>
          <VBtn
            color="white"
            variant="flat"
            class="text-primary font-weight-bold"
            :disabled="selectData.length === 0"
            :loading="viewParams.dwnLoading"
            @click="exportData"
          >
            <VIcon icon="tabler-download" class="mr-1" />匯出
          </VBtn>
        </template>
      </ListPageHeader>

      <v-card-item>
        <div class="d-flex align-center mb-1">
          <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
          <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
        </div>
        <v-row class="pt-2 pb-5">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.applyNo"
              label="申請單號"
              placeholder="輸入單號搜尋"
              prepend-inner-icon="tabler-file-search"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
            no-data-text="尚無資料"
            label="所屬計畫項目"
            :items="planItems"
            item-value="projectId"
            item-title="projectTitle"
            v-model="filters.projectId"
            clearable
            prepend-inner-icon="tabler-search"
            >
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-autocomplete
            prepend-inner-icon="tabler-search"
            no-data-text="尚無資料"
            placeholder="協會"
            :items="deptNos"
            v-model="filters.deptNo"
            clearable>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <AppInput
              v-model="filters.status"
              type="select"
              :remoteItem="deSelectSelect"
              :remoteItemParams="{ tabId}"
              item-title="label"
              item-value="value"
              placeholder="請輸入狀態"
              :disabled="false"
              :loading="false"
              clearable
            />
          </v-col>
        </v-row>
      </v-card-item>

      <InteractiveDataTable
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :items-length="filteredRows.length"
        :items="pagedItems"
        :headers="headers"
        :loading="isFetching"
        :row-clickable="false"
        :row-props="rowProps"
        show-select
        v-model="selectData"
        item-value="tbtca12Vo.applyNo"
        :item-disabled="itemDisabled"
        :item-selectable="itemDisabled"
        empty-text="尚無培訓計畫申請"
        empty-subtitle="可調整上方篩選條件後再查詢"
        empty-icon="tabler-clipboard-off"
        :items-per-page-options="[
          {value: 10, title: '10'},
          {value: 25, title: '25'},
          {value: 50, title: '50'},
          {value: 100, title: '100'},
        ]"
      >
        <template #item.applyNo="{ item }">
          <div class="d-inline-flex align-center flex-nowrap">
            <v-btn
              @click.stop="review(item.tbtca12Vo)"
              color="primary"
              variant="text"
              class="pa-0 idt-link font-weight-bold"
            >
              {{ toText(item.tbtca12Vo.applyNo) }}
            </v-btn>
            <v-tooltip location="top" text="複製單號">
              <template #activator="{ props: tipProps }">
                <v-btn
                  v-bind="tipProps"
                  variant="text"
                  icon
                  size="x-small"
                  density="compact"
                  class="ms-1"
                  @click.stop="notifyCopyDocNo(item.tbtca12Vo.applyNo)"
                >
                  <VIcon icon="tabler-copy" size="18" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>

        <template #item.deptNo="{ item }">
          <span class="font-weight-medium">{{ toText(item.duDepartmentVO.depName) }}</span>
        </template>

        <template #item.applyTime="{ item }">
          <span v-if="item.tbtca12Vo.applyTime" class="d-inline-flex align-center text-medium-emphasis text-no-wrap">
            <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
            {{ convertToROCDate(item.tbtca12Vo.applyTime,'-') }}
          </span>
          <span v-else class="text-disabled">—</span>
        </template>

        <template #item.projectTitle="{ item }">
          <span class="text-high-emphasis">{{ toText(item.tbtca10VO.projectTitle) }}</span>
        </template>

        <template #item.status="{ item }">
          <Status
            :status="item.tbtca12Vo.status"
            :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']"
            :is-green="['Passed']"
            :is-warning="['Draft', 'ReturnedForRevision']"
            :shared-table-id="tabId"
            btnClass="x-large"
            fontClass="18px"
            :status-config="getStatusData"
          />
        </template>
      </InteractiveDataTable>
    </v-card>
  </div>
</template>


<script setup lang="ts">
import { useRouter } from "vue-router";
import Status from './StatusComponenet.vue'
import { computed, reactive, watch } from "vue";
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'

definePage({
  meta: {
    action: 'manage', //固定為 manage
    subject: 'TRAININGPLANLIST', //系統代碼(系統授權設定中的系統代碼)
  },
})
const router = useRouter()
const fileUrl = useStorage('drFsUrl').value
const filters = reactive({
  applyNo: null,
  projectId: null,
  deptNo: null,
  planName: null,
  status: null,
  page: 1,
  size:10,
});

const plans = ref([]);
const headers = ref([
  { title: '申請單號', key: 'applyNo', icon: 'tabler-file-text' },
  { title: '單位', key: 'deptNo', icon: 'tabler-building-community' },
  // { title: '培訓計畫名稱', key: 'trainingPlanName' },
  { title: '申請日期', key: 'applyTime', icon: 'tabler-calendar-event' },
  { title: '計畫項目', key: 'projectTitle', icon: 'tabler-folder' },
  { title: '狀態', key: 'status', icon: 'tabler-flag' },
])
const projectId = computed(()=>filters.projectId)
const deptNo = computed(()=>filters.deptNo)
const planName = computed(()=>filters.planName)
const status = computed(()=>filters.status)
const { data: data, isFetching, execute: fetchData } = await useApi<any>(createUrl('/tca/trainingplanlist/api/training-plans-list', {
  query: {
    projectId,
    deptNo,
    planName,
    status,
    // page,
    // size,
  },
}))
const totalData = computed(() => data ? data.value.length : 0)

// client 端分頁切片（資料一次抓回，前端分頁）
const page = ref(1)
const itemsPerPage = ref(100)
const rows = computed<any[]>(() => Array.isArray(data.value) ? data.value : [])

// 申請單號為前端即時搜尋（後端清單 API 無此參數）
const filteredRows = computed<any[]>(() => {
  const kw = (filters.applyNo ?? '').toString().trim().toLowerCase()
  if (!kw) return rows.value

  return rows.value.filter(item => (item?.tbtca12Vo?.applyNo ?? '').toString().toLowerCase().includes(kw))
})

const pagedItems = computed(() => {
  if (itemsPerPage.value === -1) return filteredRows.value
  const start = (page.value - 1) * itemsPerPage.value

  return filteredRows.value.slice(start, start + itemsPerPage.value)
})

// 重新查詢或搜尋條件變動後回到第 1 頁
watch(() => filteredRows.value.length, () => {
  page.value = 1
})

const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow(
  'tnm_training_plan_list_last_clicked',
  (item: any) => item?.tbtca12Vo?.applyNo,
)

// 下拉 API
const { data: associationList = {}, execute: fetchDropdownList } = await useApi<any>(createUrl('/tca/trainingplanlist/dropdown'))
const deptNos = computed(() => associationList?.value?.itemDeptNo.map(item => {
  const [value, title] = item.split(',');
  return {
    title,
    value
  };
})) || [];
const planItems = computed(() => associationList?.value?.itemTrainingPlanName || []);

console.log("associationList",associationList);
console.log("planItems",planItems);

const viewParams = reactive({
  dwnLoading: false
});
const selectData =ref([]);
// const selectData =reactive({
//   checkedApplyNo: new Set(),
//   // checkedApplyNo = [],
//   select: (item, event)=> {
//     if(event.target.checked) {
//       selectData.checkedApplyNo.add(item.tbtca12Vo.applyNo);
//     } else {
//       selectData.checkedApplyNo.delete(item.tbtca12Vo.applyNo);
//     }
//   },
// });
const itemDisabled = (item) => {
  return item.tbtca12Vo.status == 'Approved';
}

const exportData = async(item: any) => {
  viewParams.dwnLoading = true;
  const apiUrl='/tca/trainingplanlist/api/training-plans-list/exportData'
  await $api(apiUrl, {
    method: 'POST',
    body: Array.from(selectData.value)
  }).then(async(resp) => {
    viewParams.dwnLoading = false;
    if (resp.statusCode == 200) {
      const url = await mapFileDownload(fileUrl, resp.data)
      window.open(url, '_blank');
    } else  {
      popSwal('error','匯出失敗,錯誤訊息'+resp.data);
    }
  })
};
//審議btn
const review = (item: any) => {
  setLastClickedId(item.applyNo)
  router.push({ path: '/tca/tnm/TrainingPlanApplication',query: { applyNo: item.applyNo } })
};

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  $swal({
    icon: ok ? 'success' : 'error',
    title: ok ? '已複製到剪貼簿' : '複製失敗',
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: ok ? 1500 : 2000,
  })
};

const popSwal = (icon,title) => {
  $swal({
    position: 'top-end',
    icon: icon,
    title: title,
    showConfirmButton: false,
    timer: 3000,
    customClass: {
      confirmButton: 'btn btn-primary',
    },
    buttonsStyling: false,
  });
}

const tabId = ref('TrainingPlanApplication')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))


onMounted(() => {
  fetchDropdownList();
});

</script>

<style scoped>
/* Add any custom styles here */
</style>
